import {
  AfterViewInit,
  Component,
  ComponentRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolType } from '../../../models/core/sol-type';
import { SolViewId } from '../../../models/core/sol-view-id';
import { SolViewOptions } from '../../../models/core/sol-view-options';
import { SolValidationMessage } from '../../../models/core/sol-validation-message';
import { SolViewType, ViewRegistration, ViewService } from '../../../services/view.service';
import { AbstractViewComponent, SolViewEvent } from '../abstract-view-component';
import { TimerTools } from '../../../tools/timer-tools';
import { ObjectTools } from '../../../tools/object-tools';
import { ValidationTools } from '../../../tools/validation-tools';

export interface SolViewHostData<V, O extends SolViewOptions> {
  dataType: SolType<V>;
  viewId?: SolViewId<O>;
  mode: SolViewType;
  value: V | undefined;
  viewOptions?: Partial<O>;
  messages?: SolValidationMessage[];
  immediate?: boolean;
}

class SolViewHostState<V, O extends SolViewOptions> {
  private constructor(
    public readonly data: SolViewHostData<V, O> | undefined,
    public readonly focused: boolean | undefined,
    public readonly fallbackMessage: string | undefined,
  ) {}

  public static of<V, O extends SolViewOptions>(): SolViewHostState<V, O> {
    return new SolViewHostState<V, O>(undefined, undefined, undefined);
  }

  public withData(data: SolViewHostData<V, O> | undefined): SolViewHostState<V, O> {
    if (ObjectTools.equals(this.data, data)) {
      return this;
    }
    return this.copy({ data });
  }

  public withFocused(focused: boolean | undefined): SolViewHostState<V, O> {
    if (this.focused === focused) {
      return this;
    }
    return this.copy({ focused });
  }

  public withFallbackMessage(fallbackMessage: string | undefined): SolViewHostState<V, O> {
    if (this.fallbackMessage === fallbackMessage) {
      return this;
    }
    return this.copy({ fallbackMessage });
  }

  private copy(props: Partial<SolViewHostState<V, O>>): SolViewHostState<V, O> {
    return ObjectTools.copy<SolViewHostState<V, O>>(this, props);
  }
}

@Component({
  selector: 'sol-view-host',
  imports: [],
  templateUrl: './sol-view-host.component.html',
})
export class SolViewHostComponent<V, O extends SolViewOptions>
  extends AbstractComponent<SolViewHostState<V, O>>
  implements AfterViewInit, OnDestroy
{
  // Dependencies
  private readonly viewService = inject(ViewService);

  // Outputs
  @Output('value')
  private readonly valueEmitter = new EventEmitter<V>();
  @Output('focused')
  private readonly focusedEmitter = new EventEmitter<boolean>();
  @Output('action')
  private readonly actionEmitter = new EventEmitter<SolViewEvent>();

  // Binding
  @ViewChild('view', { read: ViewContainerRef })
  private viewDiv?: ViewContainerRef;

  // Cache
  private viewRef?: ComponentRef<AbstractViewComponent<any, any>>;
  private cachedViewRegistration?: ViewRegistration<any>;

  // Computed
  protected readonly fallbackMessage$ = this.compute(state => state.fallbackMessage);

  constructor() {
    super(SolViewHostState.of());

    this.watchComputed(
      state => [state.data?.dataType, state.data?.viewId, state.data?.mode],
      () => this.updateViewComponent(),
    );
    this.watchComputed(
      state => [state.data?.value, state.data?.viewOptions, state.data?.messages],
      () => this.updateViewData(),
    );
    this.watchComputed(
      state => [state.focused],
      () => this.updateFieldFocus(),
    );
  }

  ngAfterViewInit(): void {
    this.updateViewComponent();
  }

  ngOnDestroy(): void {
    this.viewDiv?.clear();
  }

  @Input()
  public set data(data: SolViewHostData<V, O>) {
    this.update(state => state.withData(data));
  }

  @Input()
  public set focused(focused: boolean) {
    this.update(state => state.withFocused(focused));
  }

  private onValueChange(value: V) {
    const mapValueOut = this.cachedViewRegistration?.mapValueOut;
    const isEmpty = value === undefined || (value as unknown) === '';
    this.valueEmitter.emit(mapValueOut && !isEmpty ? mapValueOut(value) : value);
  }

  private onFocused(focused: boolean) {
    this.focusedEmitter.emit(focused);
  }

  private onAction(event: SolViewEvent) {
    this.actionEmitter.emit(event);
  }

  private updateViewComponent(): void {
    // Ensure container is ready
    const viewDiv = this.viewDiv;
    if (!viewDiv) {
      return;
    }

    const data = this.state.data;
    if (!data) {
      this.clearViewComponent();
      this.update(state => state.withFallbackMessage(undefined));
      return;
    }

    // Resolve view registration
    const viewRegistration = this.viewService.getView(data.dataType, data.mode, data.viewId);
    if (!viewRegistration) {
      this.clearViewComponent();
      const viewIdText = data.viewId ? `-${data.viewId.id}` : '';
      const fallbackMessage = `No view for: ${data.dataType.id}-${data.mode}${viewIdText}`;
      this.update(state => state.withFallbackMessage(fallbackMessage));
      return;
    }

    // Reuse existing component when registration is unchanged
    if (this.cachedViewRegistration === viewRegistration && this.viewRef) {
      this.update(state => state.withFallbackMessage(undefined));
      this.updateViewData();
      return;
    }

    // Create component for new registration
    this.clearViewComponent();
    const componentType = viewRegistration.componentType();
    this.cachedViewRegistration = viewRegistration;
    this.viewRef = viewDiv.createComponent(componentType as any) as ComponentRef<AbstractViewComponent<any, any>>;
    this.viewRef.instance.valueEmitter.subscribe(value => this.onValueChange(value));
    this.viewRef.instance.focusedEmitter.subscribe(focus => this.onFocused(focus));
    this.viewRef.instance.actionEmitter.subscribe(event => this.onAction(event));
    this.update(state => state.withFallbackMessage(undefined));
    this.updateViewData();
    if (this.state.focused) {
      TimerTools.nextTick(() => this.viewRef?.instance.focus());
    }
  }

  private clearViewComponent(): void {
    this.viewDiv?.clear();
    this.viewRef = undefined;
    this.cachedViewRegistration = undefined;
  }

  private updateViewData(): void {
    if (!this.viewRef || !this.cachedViewRegistration) {
      return;
    }
    const data = this.state.data;
    if (!data) {
      return;
    }

    const registration = this.cachedViewRegistration;
    const viewOptions = { ...registration.viewOptions, ...(data.viewOptions || {}) };
    const options = registration.viewId.toOptions(data.dataType, viewOptions);
    const hasError = !ValidationTools.isValid(data.messages || []);
    const value = registration.mapValue && data.value !== undefined ? registration.mapValue(data.value) : data.value;
    this.viewRef.instance.setData(value, options, hasError, data.immediate ?? false);
  }

  private updateFieldFocus(): void {
    const focused = this.state.focused;
    if (!this.viewRef || focused === undefined) {
      return;
    }

    const view = this.viewRef.instance;
    if (focused && !view.isFocused()) {
      view.focus();
    } else if (!focused && view.isFocused()) {
      view.blur();
    }
  }
}
